// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { OffsetOptions, ShiftOptions } from "@floating-ui/dom";
import {
    FloatingArrow,
    FloatingArrowProps,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    Placement,
    useFloatingParentNodeId,
    UseHoverProps,
    useTransitionStyles,
} from "@floating-ui/react";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import X from "@insite/mobius/Icons/X";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TooltipContext } from "@sc/App/Components/FloatingTooltip/TooltipContext";
import { xHeaderUpdate } from "@sc/App/Handlers/XNG/Header";
import { FocusableSelectors, useTooltip } from "@sc/App/Hooks/useTooltip";
import { addScrollbarGutter, mergeWithCss, removeScrollbarGutter } from "@sc/XDL/Utilities";
import React, { FC, FocusEvent, PropsWithChildren, useEffect, useRef, useState } from "react";

export type FloatingTooltipProps = {
    isOpen?: boolean;
    placement?: Placement;
    trigger?: "hover" | "click" | "manual";
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    offset?: OffsetOptions;
    shift?: ShiftOptions;
    delay?: UseHoverProps["delay"];
    transform?: boolean;
    fallbackAxisSideDirection?: "start" | "end" | "none" | undefined;
    showArrow?: boolean;
    overlay?: boolean;
    triggerComponent: React.ReactNode;
    enabled?: boolean;
    drawer?: boolean;
    drawerTitleIcon?: React.ComponentType | string;
    drawerTitle?: string;
    className?: string;
    extendedStyles?: FloatingTooltipStyles;
    referenceId?: string;
};

export interface FloatingTooltipStyles {
    tooltipContent?: InjectableCss;
    tooltipArrow?: Omit<FloatingArrowProps, "context">;
    drawerContent?: InjectableCss;
    drawerClose?: ButtonPresentationProps;
    triggerWrapper?: InjectableCss;
}

export const floatingTooltipStyles: FloatingTooltipStyles = {};

const FloatingTooltipComponent: FC<PropsWithChildren<FloatingTooltipProps>> = ({
    children,
    enabled = true,
    triggerComponent,
    overlay = true,
    extendedStyles,
    showArrow = true,
    drawer = false,
    referenceId,
    drawerTitleIcon,
    drawerTitle,
    className,
    trigger = "hover",
    ...options
}) => {
    const location = useAppSelector(o => getLocation(o));
    const arrowRef = useRef<SVGSVGElement>(null);
    const tooltip = useTooltip({ ...options, trigger, showArrow, arrowRef });
    const { isMounted, styles: translationStyles } = useTransitionStyles(tooltip.context, { duration: 0 });

    const [styles] = useState(() => mergeWithCss(floatingTooltipStyles, extendedStyles));
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (referenceId && typeof document !== "undefined") {
            if (!document.getElementById(referenceId)) {
                return;
            }
            tooltip.refs.setPositionReference(document.getElementById(referenceId));
        }
    }, [referenceId, isMounted]);

    useEffect(() => {
        if (overlay) {
            // XNG-Change: Register drawer with _sidepopup suffix to ensure full page overlay
            const menuName = drawer ? `${tooltip.nodeId}_sidepopup` : `${tooltip.nodeId}_tooltip`;
            dispatch(
                xHeaderUpdate({
                    key: isMounted ? "registerMenu" : "unregisterMenu",
                    value: menuName,
                }),
            );
            isMounted ? addScrollbarGutter() : removeScrollbarGutter();
            isMounted ? ariaAppHider.hide(document.body) : ariaAppHider.show(document.body);

            if (drawer) {
                isMounted ? document.body.classList.add("drawer-open") : document.body.classList.remove("drawer-open");
            }
        }
    }, [isMounted, drawer]);

    useEffect(() => {
        tooltip.setOpen(false);
        options.onOpenChange?.(false);
    }, [location.pathname]);

    useEffect(() => {
        return () => {
            tooltip.setOpen(false);
            options.onOpenChange?.(false);
            // XNG-Change: Unregister drawer with _sidepopup suffix
            const menuName = drawer ? `${tooltip.nodeId}_sidepopup` : `${tooltip.nodeId}_tooltip`;
            dispatch(
                xHeaderUpdate({
                    key: "unregisterMenu",
                    value: menuName,
                }),
            );
            removeScrollbarGutter();
            ariaAppHider.show(document.body);

            if (drawer) {
                document.body.classList.remove("drawer-open");
            }
        };
    }, [drawer]);

    if (!enabled) {
        return <>{triggerComponent}</>;
    }

    return (
        <FloatingNode id={tooltip.nodeId}>
            <TooltipContext.Provider value={{ ...tooltip, open: isMounted }}>
                <StyledWrapper
                    ref={tooltip.refs.setReference}
                    {...(trigger === "manual" ? {} : tooltip.getReferenceProps())}
                    data-state={isMounted ? "open" : "closed"}
                    {...styles.triggerWrapper}
                    as="span"
                >
                    {triggerComponent}
                </StyledWrapper>
                <FloatingPortal>
                    <StyledWrapper
                        ref={tooltip.refs.setFloating}
                        style={
                            !drawer
                                ? {
                                      ...tooltip.floatingStyles,
                                      ...translationStyles,
                                      display: isMounted ? "block" : "none",
                                  }
                                : {}
                        }
                        {...(drawer ? styles.drawerContent : styles.tooltipContent)}
                        {...{ isMounted }}
                        {...tooltip.getFloatingProps({
                            onFocus(event: FocusEvent<HTMLElement>) {
                                const target = event.target as HTMLElement;
                                if (
                                    (trigger === "hover" && target.matches(FocusableSelectors)) ||
                                    target.isContentEditable
                                ) {
                                    tooltip.setHoverEnabled(false);
                                }
                            },
                            onBlur(event: FocusEvent<HTMLElement>) {
                                const target = event.target as HTMLElement;
                                if (
                                    (trigger === "hover" && target.matches(FocusableSelectors)) ||
                                    target.isContentEditable
                                ) {
                                    tooltip.setHoverEnabled(true);
                                }
                            },
                        })}
                        className={className}
                        {...{ y: tooltip.y }}
                    >
                        {drawer && (
                            <Button
                                {...styles.drawerClose}
                                onClick={() => tooltip.setOpen(false)}
                                aria-label={translate("close")}
                            >
                                <ButtonIcon src={X} size={15} />
                            </Button>
                        )}
                        {children}
                        {!drawer && showArrow && (
                            <FloatingArrow ref={arrowRef} context={tooltip.context} {...styles.tooltipArrow} />
                        )}
                    </StyledWrapper>
                </FloatingPortal>
            </TooltipContext.Provider>
        </FloatingNode>
    );
};

export function FloatingTooltip(props: PropsWithChildren<FloatingTooltipProps>) {
    const parentId = useFloatingParentNodeId();
    if (parentId === null) {
        return (
            <FloatingTree>
                <FloatingTooltipComponent {...props} />
            </FloatingTree>
        );
    }
    return <FloatingTooltipComponent {...props} />;
}

export default FloatingTooltip;
